import { Button } from '@/components/ui/button';
import { useTranslation } from '@kgc/i18n';
import type { IPhotoStorageAdapter, OneTimeGrantSubmitPayload } from '@kgc/ui';
import { ChevronDown, ChevronRight, Inbox, Plus, Send } from 'lucide-react';
import * as React from 'react';
import { GrantCard, type GrantCardGrant } from './GrantCard';
import { GrantCreateModalRouter } from './GrantCreateModalRouter';
import type { PermanentGrantSubmitPayload } from './PermanentGrantDialog';

/**
 * AuthorizationGrantTab — STORY-1212 (B-13) Partner Detail "Meghatalmazás" tab.
 *
 * Sections (Sally 03-partner §3.3 + §3.5):
 *  - "Adott meghatalmazások" — partner a grantor (ő adja a felhatalmazást)
 *  - "Kapott meghatalmazások" — partner a grantee (ő kapja)
 *  - "+ Új grant" button — GrantCreateModalRouter (2 primary: PERMANENT/ONE_TIME)
 *  - Inline collapsed "X visszavont" link — archive view (Q4 gate-7 decision
 *    2026-04-24: inline collapsed, NEM külön tab)
 *
 * Props:
 *  - `partnerId` — the partner currently being viewed (header of PartnerDetailPage)
 *  - `grantsGiven` — grants where partnerId === grantorPartnerId
 *  - `grantsReceived` — grants where partnerId === granteePartnerId (PERMANENT only;
 *    ONE_TIME has inline granteeData, no partner FK)
 *  - `onRevoke` — revoke callback, parent handles API + audit + re-fetch
 *  - `onSubmitPermanent` / `onSubmitOneTime` — create callbacks, parent handles
 *    API + re-fetch. Omit to hide the corresponding primary button.
 *  - `photoAdapter` — required if ONE_TIME path is enabled (Photo upload
 *    dependency injection for the injected OneTimeGrantDialog)
 *
 * Filtering logic (re-renders on prop change):
 *  - Active (status === 'ACTIVE') displayed inline in each section
 *  - Terminal (REVOKED/EXPIRED/SUPERSEDED) in "archived" collapsed view
 */

export type GrantForTab = GrantCardGrant;

export interface AuthorizationGrantTabProps {
  /** The partner whose detail page we're on. */
  partnerId: string;
  /** All grants where this partner is the grantor. */
  grantsGiven: GrantForTab[];
  /** All PERMANENT grants where this partner is the grantee. */
  grantsReceived: GrantForTab[];
  onRevoke?: (grantId: string) => Promise<void> | void;
  onSubmitPermanent?: (payload: PermanentGrantSubmitPayload) => Promise<void> | void;
  onSubmitOneTime?: (payload: OneTimeGrantSubmitPayload) => Promise<void> | void;
  photoAdapter?: IPhotoStorageAdapter;
  /** Loading state passed down to GrantCard revoke button. */
  revokingGrantId?: string | null;
  /**
   * B-13 Blind Hunter P1 #2 — revoke permission predicate.
   *
   * Called per grant to decide whether to render the "Visszavonás" button.
   * The default is `false` because the component has no way to know the
   * current user's identity / privilege. The parent MUST pass a predicate
   * that checks `currentUserTenantId === grant.grantorTenantId &&
   * hasRole('PARTNER_ADMIN')` (or similar). Defaulting to `false` prevents
   * privilege-escalation UI in viewer-only contexts (e.g. support staff).
   */
  canRevokeGrant?: (grant: GrantForTab) => boolean;
}

function partitionActiveTerminal(grants: GrantForTab[]): {
  active: GrantForTab[];
  terminal: GrantForTab[];
} {
  const active: GrantForTab[] = [];
  const terminal: GrantForTab[] = [];
  for (const g of grants) {
    if (g.status === 'ACTIVE') active.push(g);
    else terminal.push(g);
  }
  return { active, terminal };
}

export function AuthorizationGrantTab({
  partnerId,
  grantsGiven,
  grantsReceived,
  onRevoke,
  onSubmitPermanent,
  onSubmitOneTime,
  photoAdapter,
  revokingGrantId,
  canRevokeGrant,
}: AuthorizationGrantTabProps) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [archiveOpen, setArchiveOpen] = React.useState(false);

  const given = React.useMemo(() => partitionActiveTerminal(grantsGiven), [grantsGiven]);
  const received = React.useMemo(() => partitionActiveTerminal(grantsReceived), [grantsReceived]);

  const archivedCount = given.terminal.length + received.terminal.length;
  const canCreate =
    (onSubmitPermanent !== undefined || onSubmitOneTime !== undefined) &&
    // if ONE_TIME enabled, photoAdapter required
    (!onSubmitOneTime || photoAdapter !== undefined);

  return (
    <div className="space-y-4" data-testid="authorization-grant-tab">
      {/* Header row: title + "+ Új grant" button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t('authGrant.tab.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('authGrant.tab.subtitle')}</p>
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="authorization-grant-tab-create"
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('authGrant.tab.createButton')}
          </Button>
        )}
      </div>

      {/* Given section */}
      <section className="space-y-2" data-testid="authorization-grant-tab-given">
        <h3 className="flex items-center gap-1 text-sm font-medium">
          <Send className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {t('authGrant.tab.givenTitle')}
          <span className="ml-1 text-xs text-muted-foreground">({given.active.length})</span>
        </h3>
        {given.active.length === 0 ? (
          <p
            className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground"
            data-testid="authorization-grant-tab-given-empty"
          >
            {t('authGrant.tab.givenEmpty')}
          </p>
        ) : (
          <div className="space-y-2">
            {given.active.map(g => (
              <GrantCard
                key={g.id}
                grant={g}
                // B-13 Blind P1 #2 — default false; parent decides via predicate.
                canRevoke={canRevokeGrant ? canRevokeGrant(g) : false}
                onRevoke={onRevoke}
                isRevoking={revokingGrantId === g.id}
                data-testid={`authorization-grant-tab-given-card-${g.id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Received section */}
      <section className="space-y-2" data-testid="authorization-grant-tab-received">
        <h3 className="flex items-center gap-1 text-sm font-medium">
          <Inbox className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {t('authGrant.tab.receivedTitle')}
          <span className="ml-1 text-xs text-muted-foreground">({received.active.length})</span>
        </h3>
        {received.active.length === 0 ? (
          <p
            className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground"
            data-testid="authorization-grant-tab-received-empty"
          >
            {t('authGrant.tab.receivedEmpty')}
          </p>
        ) : (
          <div className="space-y-2">
            {received.active.map(g => (
              <GrantCard
                key={g.id}
                grant={g}
                canRevoke={false}
                data-testid={`authorization-grant-tab-received-card-${g.id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Archived — inline collapsed (Q4 gate-7 decision) */}
      {archivedCount > 0 && (
        <section className="border-t pt-3" data-testid="authorization-grant-tab-archive">
          <button
            type="button"
            onClick={() => setArchiveOpen(v => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            aria-expanded={archiveOpen}
            data-testid="authorization-grant-tab-archive-toggle"
          >
            {archiveOpen ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
            {t('authGrant.tab.archivedToggle', { count: archivedCount })}
          </button>
          {archiveOpen && (
            <div className="mt-2 space-y-2">
              {given.terminal.map(g => (
                <GrantCard
                  key={g.id}
                  grant={g}
                  archived
                  data-testid={`authorization-grant-tab-archive-card-${g.id}`}
                />
              ))}
              {received.terminal.map(g => (
                <GrantCard
                  key={g.id}
                  grant={g}
                  archived
                  data-testid={`authorization-grant-tab-archive-card-${g.id}`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Reference partnerId to silence unused-prop linting in minimal scope;
          in production the parent derives grantsGiven/grantsReceived from it. */}
      {process.env.NODE_ENV === 'test' && (
        <span className="sr-only" data-testid="authorization-grant-tab-partner-id">
          {partnerId}
        </span>
      )}

      {canCreate && (
        <GrantCreateModalRouter
          open={createOpen}
          onOpenChange={setCreateOpen}
          {...(onSubmitPermanent ? { onSubmitPermanent } : {})}
          {...(onSubmitOneTime ? { onSubmitOneTime } : {})}
          {...(photoAdapter ? { photoAdapter } : {})}
        />
      )}
    </div>
  );
}
